import React, { useState } from "react";
import {
  Box,
  Button,
  Drawer,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  Grid,
  Snackbar,
  FormControlLabel,
  Switch,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Tabs,
  Tab,
  Avatar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  MoreVert,
  Delete,
  Refresh,
  Image as ImageIcon,
  Link as LinkIcon,
  Edit,
  Close,
  Search,
  FilterList,
  ArrowBack,
  Add
} from '@mui/icons-material';
import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import {
  useListBannersQuery,
  useAddBannerMutation,
  useUpdateBannerMutation,
  useDeleteBannerMutation,
  Banner as BannerType,
} from '../../features/admin/bannerApi.ts';

const Banner = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [searchStr, setSearchStr] = useState('');
  const [tabValue, setTabValue] = useState(0);

  // Form states
  const [caption, setCaption] = useState('');
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [active, setActive] = useState(true);
  const [cta, setCta] = useState('');
  const [file, setFile] = useState<File | null>(null);

  // Edit mode states
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState<BannerType | null>(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // API hooks
  const {
    data,
    isLoading,
    error,
    isError,
    refetch
  } = useListBannersQuery();

  const [addBanner, { isLoading: isAdding }] = useAddBannerMutation();
  const [updateBanner, { isLoading: isUpdating }] = useUpdateBannerMutation();
  const [deleteBanner, { isLoading: isDeleting }] = useDeleteBannerMutation();
  const [productId, setProductId] = useState('');

  const banners = data?.banners || [];
  const bannersPerPage = 8;
  const totalRecords = banners.length;
  const totalPages = Math.ceil(totalRecords / bannersPerPage);

  // Paginate banners
  const paginatedBanners = banners.slice(
    (page - 1) * bannersPerPage,
    page * bannersPerPage
  );

  const isEditing = Boolean(editingBanner);

  // Helper function to get the correct image path
  const getImageUrl = (banner: BannerType) => {
    const possibleFields = [
      'banner_image',
      'file_path',
      'filename',
      'image',
      'file',
      'path',
      'image_path',
      'banner_image_path'
    ];

    let imagePath = null;

    for (const field of possibleFields) {
      if (banner[field as keyof BannerType]) {
        imagePath = banner[field as keyof BannerType] as string;
        break;
      }
    }

    if (!imagePath) {
      return '';
    }

    let cleanPath = imagePath.trim();

    if (cleanPath.startsWith('http://') || cleanPath.startsWith('https://')) {
      return cleanPath;
    }

    if (cleanPath.includes('/')) {
      const parts = cleanPath.split('/');
      cleanPath = parts[parts.length - 1];
    }

    if (cleanPath.includes('\\')) {
      const parts = cleanPath.split('\\');
      cleanPath = parts[parts.length - 1];
    }

    const baseUrl = 'https://fftestservice.sarsetech.com';
    const imageUrl = `${baseUrl}/images/${cleanPath}`;

    return imageUrl;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];

      // Check file size (limit to 2MB)
      const maxSize = 2 * 1024 * 1024;
      if (selectedFile.size > maxSize) {
        setSnackbar({
          open: true,
          message: `File size too large (${(selectedFile.size / 1024 / 1024).toFixed(2)}MB). Please select an image smaller than 2MB.`,
          severity: 'error',
        });
        e.target.value = '';
        return;
      }

      // Check file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(selectedFile.type)) {
        setSnackbar({
          open: true,
          message: 'Invalid file type. Please select a JPG, PNG, or WebP image.',
          severity: 'error',
        });
        e.target.value = '';
        return;
      }

      setFile(selectedFile);
    }
  };

  const resetForm = () => {
    setCaption('');
    setTitle('');
    setStartDate('');
    setEndDate('');
    setActive(true);
    setCta('');
    setFile(null);
    setEditingBanner(null);

    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const handleAddBanner = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEditBanner = (banner: BannerType) => {
    setCaption(banner.caption || '');
    setTitle(banner.title || '');
    setStartDate(banner.start_date ? banner.start_date.split('T')[0] : '');
    setEndDate(banner.end_date ? banner.end_date.split('T')[0] : '');
    setActive(banner.active === 1 || banner.active === '1' || banner.active === true);
    setCta(banner.cta || '');
    setProductId(banner.product_id?.toString() || '');
    setFile(null);
    setEditingBanner(banner);
    setShowForm(true);
  };

  const handleBackToList = () => {
    setShowForm(false);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!caption.trim()) {
      setSnackbar({
        open: true,
        message: 'Please provide a caption',
        severity: 'error',
      });
      return;
    }

    if (!title.trim()) {
      setSnackbar({
        open: true,
        message: 'Please provide a title',
        severity: 'error',
      });
      return;
    }

    if (!startDate) {
      setSnackbar({
        open: true,
        message: 'Please provide a start date',
        severity: 'error',
      });
      return;
    }

    if (!endDate) {
      setSnackbar({
        open: true,
        message: 'Please provide an end date',
        severity: 'error',
      });
      return;
    }

    if (!cta.trim()) {
      setSnackbar({
        open: true,
        message: 'Please provide a CTA URL',
        severity: 'error',
      });
      return;
    }

    if (!file && !isEditing) {
      setSnackbar({
        open: true,
        message: 'Please select an image',
        severity: 'error',
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append('caption', caption);
      formData.append('title', title);
      formData.append('start_date', startDate);
      formData.append('end_date', endDate);
      formData.append('active', active ? '1' : '0');
      formData.append('cta', cta);
      formData.append('product_id', productId);
      formData.append('mode', 'main');

      if (file) {
        formData.append('banner_image', file);
      }

      if (isEditing && editingBanner) {
        await updateBanner({ id: editingBanner.id, formData }).unwrap();
        setSnackbar({
          open: true,
          message: 'Banner updated successfully!',
          severity: 'success',
        });
      } else {
        await addBanner(formData).unwrap();
        setSnackbar({
          open: true,
          message: 'Banner added successfully!',
          severity: 'success',
        });
      }

      handleBackToList();
      refetch();
    } catch (err: any) {
      console.error('Failed to save banner:', err);
      setSnackbar({
        open: true,
        message: err?.data?.message || `Failed to ${isEditing ? 'update' : 'add'} banner`,
        severity: 'error',
      });
    }
  };

  const handleDeleteBanner = async (bannerId: number) => {
    if (window.confirm('Are you sure you want to delete this banner?')) {
      try {
        await deleteBanner(bannerId).unwrap();
        setSnackbar({
          open: true,
          message: 'Banner deleted successfully!',
          severity: 'success',
        });
        refetch();
      } catch (err: any) {
        console.error('Failed to delete banner:', err);
        setSnackbar({
          open: true,
          message: err?.data?.message || 'Failed to delete banner',
          severity: 'error',
        });
      }
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(event.target.value);
    setPage(1);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const renderStatus = (active: boolean) => {
    return active ? (
      <Chip label="Active" size="small" sx={{ bgcolor: '#d1f4e0', color: '#00783e', fontWeight: 600, fontSize: '12px' }} />
    ) : (
      <Chip label="Inactive" size="small" sx={{ bgcolor: '#e4e5e7', color: '#616161', fontWeight: 600, fontSize: '12px' }} />
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar for desktop */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Drawer for mobile  */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main conten area */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            {!showForm ? (
              // LIST VIEW
              <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Banners
                  </Typography>

                  <Button
                    variant="contained"
                    onClick={handleAddBanner}
                    sx={{
                      bgcolor: '#202223',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '8px',
                      px: 2.5,
                      '&:hover': { bgcolor: '#303030' }
                    }}
                  >
                    Add banner
                  </Button>
                </Box>

                <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                  <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                    <Tabs
                      value={tabValue}
                      onChange={(e, v) => setTabValue(v)}
                      sx={{
                        px: 2,
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '14px',
                          minHeight: 48,
                          color: '#6d7175',
                        },
                        '& .Mui-selected': {
                          color: '#202223',
                          fontWeight: 600,
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: '#202223',
                          height: 3,
                        }
                      }}
                    >
                      <Tab label={`All (${totalRecords})`} />

                    </Tabs>
                  </Box>

                  <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Search banners"
                      value={searchStr}
                      onChange={handleSearch}
                      fullWidth
                      sx={{
                        maxWidth: 400,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          bgcolor: 'white',
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" sx={{ color: '#6d7175' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<FilterList />}
                      sx={{
                        textTransform: 'none',
                        borderColor: '#c9cccf',
                        color: '#202223',
                        borderRadius: '8px',
                        fontWeight: 500,
                        '&:hover': {
                          borderColor: '#202223',
                          bgcolor: 'transparent',
                        }
                      }}
                    >
                      Filter
                    </Button>
                  </Box>

                  {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                      <CircularProgress size={32} sx={{ color: '#008060' }} />
                    </Box>
                  )}

                  {isError && !isLoading && (
                    <Box sx={{ p: 3, bgcolor: 'white' }}>
                      <Alert severity="error">Failed to load banners. Please try again.</Alert>
                    </Box>
                  )}

                  {!isLoading && !isError && paginatedBanners.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                        No banners found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {searchStr ? `No results for "${searchStr}"` : 'Get started by adding your first banner'}
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={handleAddBanner}
                        sx={{
                          bgcolor: '#202223',
                          color: 'white',
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: '8px',
                          '&:hover': { bgcolor: '#303030' }
                        }}
                      >
                        Add banner
                      </Button>
                    </Box>
                  )}

                  {!isLoading && paginatedBanners.length > 0 && (
                    <Box sx={{ bgcolor: 'white' }}>
                      {/* Table Header */}

                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: '50px 100px 1fr 120px 200px 150px 120px 100px', // Updated from 7 to 8 columns
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb'
                      }}>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          #
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Images
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Title
                        </Typography>
                        {/* Add Product ID column */}
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Product ID
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Dates
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Status
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          CTA
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>
                          Actions
                        </Typography>
                      </Box>

                      {/* Table Rows */}
                      {paginatedBanners.map((banner: BannerType, index: number) => {
                        const imageUrl = getImageUrl(banner);
                        const isBannerActive = banner.active === 1 || banner.active === '1' || banner.active === true;

                        return (
                          <Box
                            key={banner.id}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '50px 100px 1fr 120px 200px 150px 120px 100px', // Updated from 7 to 8 columns
                              gap: 2,
                              px: 2,
                              py: 2,
                              alignItems: 'center',
                              borderBottom: index < paginatedBanners.length - 1 ? '1px solid #e1e3e5' : 'none',
                              '&:hover': { bgcolor: '#f9fafb' },
                              transition: 'background-color 0.15s ease',
                            }}
                          >
                            <Typography variant="body2" sx={{ color: '#6d7175' }}>
                              {(page - 1) * bannersPerPage + index + 1}
                            </Typography>

                            <Avatar
                              src={imageUrl}
                              variant="rounded"
                              sx={{ width: 60, height: 40, bgcolor: '#f1f2f4' }}
                            >
                              <ImageIcon />
                            </Avatar>

                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                                {banner.title || 'N/A'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {banner.caption || 'No caption'}
                              </Typography>
                            </Box>

                            {/* Add Product ID cell */}
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#202223',
                                fontWeight: 500,
                                fontSize: '14px'
                              }}
                            >
                              {banner.product_id || 'N/A'}
                            </Typography>

                            <Box>
                              <Typography variant="body2" sx={{ fontSize: '12px', color: '#202223' }}>
                                {formatDate(banner.start_date)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                to {formatDate(banner.end_date)}
                              </Typography>
                            </Box>

                            <Box>
                              {renderStatus(isBannerActive)}
                            </Box>

                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: '12px'
                              }}
                            >
                              {banner.cta || 'No CTA'}
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <IconButton
                                size="small"
                                onClick={() => handleEditBanner(banner)}
                                sx={{
                                  color: '#6d7175',
                                  '&:hover': { bgcolor: '#f1f2f4' }
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteBanner(banner.id)}
                                disabled={isDeleting} 
                                sx={{
                                  color: '#6d7175',
                                  '&:hover': { bgcolor: '#f1f2f4' }
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Card>

                {totalPages > 1 && !isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handleChangePage}
                      sx={{
                        '& .MuiPaginationItem-root': {
                          borderRadius: '8px',
                          fontWeight: 500,
                        },
                        '& .Mui-selected': {
                          bgcolor: '#202223 !important',
                          color: 'white',
                        }
                      }}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              // FORM VIEW
              <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBackToList}
                    sx={{
                      color: '#202223',
                      textTransform: 'none',
                      fontWeight: 500,
                      mb: 2,
                      '&:hover': { bgcolor: 'transparent' }
                    }}
                  >
                    Banners
                  </Button>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    {isEditing ? 'Edit banner' : 'Add banner'}
                  </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Stack spacing={2}>
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Banners details
                            </Typography  >
                            <TextField
                              label="Title"
                              value={title}
                              onChange={(e) => setTitle(e.target.value)}
                              fullWidth
                              required
                              placeholder="Enter banner title"
                              disabled={isAdding || isUpdating}
                              sx={{ mb: 2 }}
                            />

                            <TextField
                              label="Caption"
                              value={caption}
                              onChange={(e) => setCaption(e.target.value)}
                              fullWidth
                              required
                              placeholder="Enter banner caption"
                              disabled={isAdding || isUpdating}
                              sx={{ mb: 2 }}
                            />

                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="Start Date"
                                  type="date"
                                  value={startDate}
                                  onChange={(e) => setStartDate(e.target.value)}
                                  fullWidth
                                  required
                                  disabled={isAdding || isUpdating}
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="End Date"
                                  type="date"
                                  value={endDate}
                                  onChange={(e) => setEndDate(e.target.value)}
                                  fullWidth
                                  required
                                  disabled={isAdding || isUpdating}
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                />
                              </Grid>
                            </Grid>

                            <TextField
                              label="CTA URL"
                              value={cta}
                              onChange={(e) => setCta(e.target.value)}
                              fullWidth
                              required
                              placeholder="https://example.com/product"
                              disabled={isAdding || isUpdating}
                              sx={{ mt: 2 }}
                              helperText="Enter the URL where users will be redirected when clicking the banner"
                            />

                            {/* Add Product ID field with spacing */}
                            <Box sx={{ mt: 2 }}>
                              <TextField
                                label="Product ID"
                                value={productId}
                                onChange={(e) => setProductId(e.target.value)}
                                fullWidth
                                placeholder="Enter product ID (optional)"
                                disabled={isAdding || isUpdating}
                                type="number"
                                helperText="Optional: Associate this banner with a specific product"
                              />
                            </Box>
                          </Box>
                        </Card>

                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Banner Image
                            </Typography>

                            <Box sx={{
                              border: '2px dashed #c9cccf',
                              borderRadius: '12px',
                              p: 4,
                              textAlign: 'center',
                              cursor: 'pointer',
                              bgcolor: '#fafbfb',
                              transition: 'all 0.2s',
                              '&:hover': {
                                borderColor: '#202223',
                                bgcolor: '#f6f6f7'
                              }
                            }}
                              onClick={() => document.getElementById('banner-image-upload')?.click()}>
                              {file ? (
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                  <Box
                                    component="img"
                                    src={URL.createObjectURL(file)}
                                    sx={{
                                      width: 120,
                                      height: 80,
                                      objectFit: 'cover',
                                      borderRadius: '8px'
                                    }}
                                  />
                                  <IconButton
                                    sx={{
                                      position: 'absolute',
                                      top: -8,
                                      right: -8,
                                      bgcolor: 'white',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                      '&:hover': { bgcolor: '#f5f5f5' }
                                    }}
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setFile(null);
                                    }}
                                  >
                                    <Close fontSize="small" />
                                  </IconButton>
                                </Box>
                              ) : editingBanner && getImageUrl(editingBanner) ? (
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                  <Avatar
                                    src={getImageUrl(editingBanner)}
                                    variant="rounded"
                                    sx={{ width: 120, height: 80 }}
                                  />
                                  <Typography variant="caption" display="block" sx={{ color: '#6d7175', mt: 1 }}>
                                    Current image - click to change
                                  </Typography>
                                </Box>
                              ) : (
                                <Box>
                                  <Box sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: '8px',
                                    bgcolor: 'white',
                                    border: '1px solid #e1e3e5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                    mb: 2
                                  }}>
                                    <Add sx={{ fontSize: 24, color: '#6d7175' }} />
                                  </Box>
                                  <Button
                                    variant="outlined"
                                    sx={{
                                      textTransform: 'none',
                                      borderColor: '#c9cccf',
                                      color: '#202223',
                                      fontWeight: 500,
                                      borderRadius: '8px',
                                      '&:hover': {
                                        borderColor: '#202223',
                                        bgcolor: 'transparent',
                                      }
                                    }}
                                  >
                                    Add image
                                  </Button>
                                  <Typography variant="caption" display="block" sx={{ color: '#6d7175', mt: 1 }}>
                                    Maximum file size 2MB. Supported formats: JPG, PNG, WebP
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            <input
                              id="banner-image-upload"
                              type="file"
                              accept="image/jpeg,image/jpg,image/png,image/webp"
                              onChange={handleFileChange}
                              style={{ display: 'none' }}
                              disabled={isAdding || isUpdating}
                            />
                          </Box>
                        </Card>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                        <Box sx={{ p: 3 }}>
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                            Status
                          </Typography>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={active}
                                onChange={(e) => setActive(e.target.checked)}
                                disabled={isAdding || isUpdating}
                                color="primary"
                              />
                            }
                            label={active ? "Active" : "Inactive"}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {active ? "Banner will be visible to users" : "Banner will be hidden from users"}
                          </Typography>
                        </Box>
                      </Card>
                    </Grid>
                  </Grid>

                  <Box sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'flex-end',
                    mt: 3,
                    pt: 3,
                    borderTop: '1px solid #e1e3e5'
                  }}>
                    <Button
                      onClick={handleBackToList}
                      disabled={isAdding || isUpdating}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,

                        
                        color: '#202223',
                        borderRadius: '8px',
                        px: 2.5
                      }}
                    >
                      Discard
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isAdding || isUpdating || !caption.trim() || !title.trim() || !startDate || !endDate || !cta.trim() || (!file && !isEditing)}
                      sx={{
                        bgcolor: '#008060',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        px: 2.5,
                        '&:hover': { bgcolor: '#006e52' }
                      }}
                    >
                      {(isAdding || isUpdating) ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={20} color="inherit" />
                          {isEditing ? 'Updating...' : 'Adding...'}
                        </Box>
                      ) : (
                        isEditing ? 'Update banner' : 'Add banner'
                      )}
                    </Button>
                  </Box>
                </form>
               </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Banner;